import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Input,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";
import { X, PlayCircle, Plus } from "lucide-react";
import { useSearchPanelStyles } from "./style";
import { showToast } from "../../../../utils/toast";

export interface UploadMediaPostModalMeta {
  isPaidContent: boolean;
  price: string;
}

export interface UploadMediaPostModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (files: File[], meta?: UploadMediaPostModalMeta) => void;
  /** Show "Paid content" switch and price input (e.g. for create post). Default true. */
  showPaidContent?: boolean;
  /** Require content provider agreement before selecting files. Default true. */
  showAgreement?: boolean;
  /** When showAgreement is true, pass parent's agreement state. */
  agreementAccepted?: boolean;
  /** Max number of files. Default 10. Use 1 for single file (e.g. messages). */
  maxFiles?: number;
  /** Prefill when reopening (e.g. previously attached files). */
  initialFiles?: File[];
  /** Prefill paid content state when reopening. */
  initialMeta?: UploadMediaPostModalMeta;
}

const isVideoFile = (file: File) => file.type.startsWith("video/");

export function UploadMediaPostModal({
  open,
  onClose,
  onConfirm,
  showPaidContent = true,
  showAgreement = true,
  agreementAccepted: agreementAcceptedProp,
  maxFiles = 10,
  initialFiles = [],
  initialMeta,
}: UploadMediaPostModalProps) {
  const classes = useSearchPanelStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<string[]>([]);
  const [isPaidContent, setIsPaidContent] = useState(false);
  const [price, setPrice] = useState("");
  const [agreementAcceptedInternal] = useState(true);
  const [draggedFileIndex, setDraggedFileIndex] = useState<number | null>(null);
  const agreementAccepted =
    agreementAcceptedProp !== undefined
      ? agreementAcceptedProp
      : agreementAcceptedInternal;

  const canAddFiles = showAgreement ? agreementAccepted : true;

  useEffect(() => {
    if (!open) return;
    if (initialFiles.length > 0) {
      setSelectedFiles([...initialFiles]);
      setFilePreviews(initialFiles.map(f => URL.createObjectURL(f)));
    } else {
      setSelectedFiles([]);
      setFilePreviews([]);
    }
    setPrice(initialMeta?.price || "");
    setIsPaidContent(Boolean(initialMeta?.isPaidContent));
  }, [open, initialFiles, initialMeta]);

  const handleClose = () => {
    filePreviews.forEach(url => URL.revokeObjectURL(url));
    setSelectedFiles([]);
    setFilePreviews([]);
    onClose();
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;
    if (showAgreement && !agreementAccepted) {
      showToast(
        "Please accept the Content Provider Agreement before uploading content.",
        "error"
      );
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    const validFiles: File[] = [];
    const previews: string[] = [];
    const remaining = maxFiles - selectedFiles.length;
    if (remaining <= 0) return;
    Array.from(files)
      .slice(0, remaining)
      .forEach(file => {
        if (file.type.startsWith("image/") || file.type.startsWith("video/")) {
          validFiles.push(file);
          previews.push(URL.createObjectURL(file));
        }
      });
    setSelectedFiles(prev => [...prev, ...validFiles]);
    setFilePreviews(prev => [...prev, ...previews]);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    if (e.target) e.target.value = "";
  };

  const handleRemoveFile = (index: number) => {
    URL.revokeObjectURL(filePreviews[index]);
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setFilePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeleteAll = () => {
    filePreviews.forEach(url => URL.revokeObjectURL(url));
    setSelectedFiles([]);
    setFilePreviews([]);
  };

  const handleDragStartFile = (index: number) => {
    setDraggedFileIndex(index);
  };

  const handleDragOverFile = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDropFile = (targetIndex: number) => {
    if (draggedFileIndex === null || draggedFileIndex === targetIndex) {
      setDraggedFileIndex(null);
      return;
    }

    setSelectedFiles(prev => {
      const next = [...prev];
      const [moved] = next.splice(draggedFileIndex, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });

    setFilePreviews(prev => {
      const next = [...prev];
      const [moved] = next.splice(draggedFileIndex, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });

    setDraggedFileIndex(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (showAgreement && !agreementAccepted) {
      showToast(
        "Please accept the Content Provider Agreement before uploading content.",
        "error"
      );
      return;
    }
    handleFileSelect(e.dataTransfer.files);
    e.dataTransfer.clearData();
  };

  const handleConfirm = () => {
    if (showAgreement && !agreementAccepted) {
      showToast(
        "Please accept the Content Provider Agreement before uploading content.",
        "error"
      );
      return;
    }
    if (selectedFiles.length === 0) return;
    const meta: UploadMediaPostModalMeta | undefined = showPaidContent
      ? { isPaidContent, price }
      : undefined;
    onConfirm([...selectedFiles], meta);
    handleClose();
  };

  const openFileInput = () => {
    if (showAgreement && !agreementAccepted) {
      showToast(
        "Please accept the Content Provider Agreement before uploading content.",
        "error"
      );
      return;
    }
    fileInputRef.current?.click();
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        className: classes.mediaModalPaper,
        style: {
          backgroundColor: "#131313",
          borderRadius: "12px",
          width: isMobile
            ? "calc(100% - 42px)"
            : selectedFiles.length > 0
            ? "780px"
            : "480px",
          maxWidth: isMobile
            ? "calc(100% - 42px)"
            : selectedFiles.length > 0
            ? "780px"
            : "480px",
          margin: isMobile ? "0 21px" : "auto",
          height: selectedFiles.length === 0 && !isMobile ? "500px" : "auto",
          maxHeight: selectedFiles.length === 0 && !isMobile ? "500px" : "90vh",
        },
      }}
    >
      <DialogTitle
        className={classes.mediaModalTitle}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: 0,
          marginBottom: isMobile ? "0" : "0",
        }}
      >
        <Typography variant="h6" className={classes.mediaModalTitleText}>
          Upload your media
        </Typography>
        <IconButton
          onClick={handleClose}
          className={classes.mediaModalCloseButton}
          size="small"
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        className={classes.mediaModalContent}
        style={{
          display: "flex",
          flexDirection: "column",
          height:
            selectedFiles.length === 0 && !isMobile
              ? "calc(500px - 73px)"
              : "auto",
          overflow:
            selectedFiles.length === 0 && !isMobile ? "hidden" : "visible",
        }}
      >
        {selectedFiles.length > 0 && (
          <Box>
            {!isMobile && (
              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "12px",
                }}
              >
                <Typography className={classes.uploadedContentTitle}>
                  Uploaded content
                </Typography>
                {!isMobile && selectedFiles.length < maxFiles && maxFiles > 1 && (
                  <Button
                    className={classes.addMediaTopButton}
                    onClick={openFileInput}
                    startIcon={<Plus size={20} />}
                    disabled={
                      !canAddFiles || selectedFiles.length >= maxFiles
                    }
                  >
                    Add media
                  </Button>
                )}
              </Box>
            )}
            {isMobile && maxFiles > 1 && (
              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  marginBottom: "12px",
                }}
              >
                <Button
                  className={classes.deleteAllButton}
                  onClick={handleDeleteAll}
                >
                  Delete all
                </Button>
              </Box>
            )}
            <Box
              className={classes.selectedFilesContainer}
              style={{ marginBottom: "8px", position: "relative" }}
            >
              {!isMobile && <Box className={classes.filesGradientOverlay} />}
              <Box className={classes.selectedFilesList}>
                {selectedFiles.map((file, index) => (
                  <Box
                    key={`${file.name}-${index}`}
                    className={classes.filePreviewItem}
                    draggable
                    onDragStart={() => handleDragStartFile(index)}
                    onDragOver={handleDragOverFile}
                    onDrop={() => handleDropFile(index)}
                    onDragEnd={() => setDraggedFileIndex(null)}
                    style={{ cursor: "grab" }}
                  >
                    {isVideoFile(file) ? (
                      <Box className={classes.videoPreview}>
                        <video
                          src={filePreviews[index]}
                          className={classes.filePreview}
                        />
                        <Box className={classes.playButtonOverlay}>
                          <PlayCircle size={24} color="#FFFFFF" />
                        </Box>
                      </Box>
                    ) : (
                      <img
                        src={filePreviews[index]}
                        alt={`Preview ${index}`}
                        className={classes.filePreview}
                      />
                    )}
                    <IconButton
                      className={classes.removeFileButton}
                      onClick={() => handleRemoveFile(index)}
                      size="small"
                    >
                      <X size={16} color="#FFFFFF" />
                    </IconButton>
                  </Box>
                ))}
                {selectedFiles.length < maxFiles && (
                  <Box
                    className={classes.addFileButton}
                    onClick={openFileInput}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  >
                    <Plus size={24} color="#FFFFFF" />
                  </Box>
                )}
              </Box>
            </Box>
            {maxFiles > 1 && (
              <Typography className={classes.filesLimitText}>
                {selectedFiles.length}/{maxFiles} pictures limit
              </Typography>
            )}
          </Box>
        )}

        {showPaidContent && selectedFiles.length > 0 && (
          <Box
            className={classes.modalPaidContentContainer}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: isMobile ? "space-between" : "space-between",
              marginTop: "20px",
              marginBottom: isMobile && isPaidContent ? "12px" : "0",
              gap: "12px",
            }}
          >
            <Box style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Box
                className={`${classes.paidContentSwitcher} ${
                  isPaidContent ? classes.paidContentSwitcherActive : ""
                }`}
                onClick={() => setIsPaidContent(!isPaidContent)}
              >
                <Box className={classes.paidContentSwitcherThumb} />
              </Box>
              <Typography className={classes.modalPaidContentLabel}>
                Paid content
              </Typography>
            </Box>
            {!isMobile && isPaidContent && (
              <Box className={classes.modalPriceInputContainer}>
                <Box className={classes.modalPriceInputWrapper}>
                  <Typography className={classes.modalPriceInputLabel}>
                    Price
                  </Typography>
                  <Input
                    className={classes.modalPriceInput}
                    placeholder="$5.00"
                    disableUnderline
                    value={price}
                    onChange={e => {
                      const value = e.target.value;
                      if (value === "" || /^\d*\.?\d*$/.test(value)) {
                        setPrice(value);
                      }
                    }}
                    onClick={e => e.stopPropagation()}
                  />
                  <Typography className={classes.modalPriceLabel}>
                    Minimum price: $5
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {showPaidContent && isMobile && isPaidContent && selectedFiles.length > 0 && (
          <Box className={classes.mobilePriceInputContainer}>
            <Box className={classes.modalPriceInputWrapper}>
              <Typography className={classes.modalPriceInputLabel}>
                Price
              </Typography>
              <Input
                className={classes.modalPriceInput}
                placeholder="$5.00"
                disableUnderline
                value={price}
                onChange={e => {
                  const value = e.target.value;
                  if (value === "" || /^\d*\.?\d*$/.test(value)) setPrice(value);
                }}
                onClick={e => e.stopPropagation()}
                style={{ width: "100%" }}
              />
              <Typography className={classes.modalPriceLabel}>
                Minimum price: $5
              </Typography>
            </Box>
          </Box>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          multiple={maxFiles > 1}
          onChange={handleFileInputChange}
          style={{ display: "none" }}
          disabled={showAgreement && !agreementAccepted}
        />

        {selectedFiles.length === 0 && (
          <Box
            className={classes.uploadSection}
            style={{
              cursor: canAddFiles ? "pointer" : "not-allowed",
              opacity: canAddFiles ? 1 : 0.5,
            }}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={openFileInput}
          >
            <Typography className={classes.uploadClickText}>
              Click to upload
            </Typography>
            <Typography className={classes.uploadDragText}>
              or drag and drop SVG, PNG, GIF or MP4 (max. 800×400 px)
            </Typography>
          </Box>
        )}

        {selectedFiles.length > 0 && (
          <Box className={classes.modalButtonsContainer}>
            {isMobile ? (
              <Box
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  width: "100%",
                }}
              >
                {maxFiles > 1 && (
                  <Button
                    className={classes.addMoreButton}
                    onClick={openFileInput}
                    fullWidth
                    startIcon={<Plus size={20} />}
                    disabled={!canAddFiles || selectedFiles.length >= maxFiles}
                  >
                    Add more media
                  </Button>
                )}
                <Button
                  className={classes.continueButton}
                  onClick={handleConfirm}
                  fullWidth
                >
                  Continue
                </Button>
              </Box>
            ) : (
              <Box
                style={{ display: "flex", gap: "12px", width: "100%" }}
              >
                <Button
                  className={classes.cancelButton}
                  onClick={handleClose}
                  fullWidth
                >
                  Cancel
                </Button>
                <Button
                  className={classes.addButton}
                  onClick={handleConfirm}
                  fullWidth
                >
                  Add Media
                </Button>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default UploadMediaPostModal;
